import React, { useState, useRef, useEffect } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { BulkImageEditModal } from './BulkImageEditModal';
import { LoadingOverlay } from '../common/LoadingOverlay';
import type { ProcessingProgress } from '../../utils/imageProcessing';
import { processImageGroups } from '../../utils/imageImportUtils';
import { useAuthStore } from '../../stores/authStore';
import { useNavigate } from 'react-router-dom';

interface BulkImageImportProps {
  onComplete?: () => void;
}

export function BulkImageImport({ onComplete }: BulkImageImportProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ProcessingProgress | null>(null);
  const [processedImages, setProcessedImages] = useState<{
    files: File[];
    originals: string[];
    thumbnails: string[];
  } | null>(null);
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [isBulkImportAllowed, setIsBulkImportAllowed] = useState(true);

  useEffect(() => {
    if (!user) {
      setIsBulkImportAllowed(false);
      return;
    }
    const hasPlan = user.plan !== 'unsubscribed';
    setIsBulkImportAllowed(hasPlan);
  }, [user]);

  const handleUpgradeClick = () => {
    navigate('/pricing');
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isBulkImportAllowed) {
      setError('一括インポート機能はプラン加入後にご利用いただけます');
      setTimeout(() => setError(null), 5000);
      return;
    }

    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsLoading(true);
    setError(null);
    setProgress(null);

    try {
      const [group] = await processImageGroups(files, (progress) => {
        setProgress(progress);
      });

      if (!group || group.files.length === 0) {
        throw new Error('有効な画像が見つかりませんでした');
      }

      setProcessedImages({
        files: group.files,
        originals: group.originals,
        thumbnails: group.thumbnails
      });

      setShowEditModal(true);
    } catch (error) {
      console.error('Error processing images:', error);
      setError(error instanceof Error ? error.message : '画像の処理中にエラーが発生しました');
    } finally {
      setIsLoading(false);
      setProgress(null);
    }
  };

  const handleComplete = () => {
    if (onComplete && typeof onComplete === 'function') {
      onComplete();
    }
    setShowEditModal(false);
    setProcessedImages(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={isLoading || !isBulkImportAllowed}
        className={`w-full flex items-center justify-center gap-2 px-4 py-3 h-12 rounded-md text-sm font-medium transition-colors ${
          isLoading || !isBulkImportAllowed
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
          onClick={e => {
            (e.target as HTMLInputElement).value = '';
          }}
        />
        {isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Upload className="w-5 h-5" />
        )}
        <span>一括で写真をインポート</span>
      </button>

      {!isBulkImportAllowed && (
        <div className="mt-2 p-3 bg-amber-50 text-amber-700 text-sm rounded-md flex flex-col">
          <p className="mb-2">一括インポート機能はプラン加入後にご利用いただけます</p>
          <button
            onClick={handleUpgradeClick}
            className="self-end px-3 py-1 bg-amber-100 text-amber-800 rounded hover:bg-amber-200 transition-colors text-xs font-medium"
          >
            プランを確認する
          </button>
        </div>
      )}

      {error && (
        <div className="mt-2 p-3 bg-red-50 text-red-600 text-sm rounded-md">
          {error}
        </div>
      )}

      {showEditModal && processedImages && (
        <BulkImageEditModal
          images={processedImages}
          onClose={handleComplete}
        />
      )}

      {isLoading && (
        <LoadingOverlay
          message="商品画像を取り込んでいます。今しばらくお待ちください..."
          progress={progress || undefined}
        />
      )}
    </div>
  );
}
